/**
 * @fileoverview Recoveries View — Proof of Debt adjudication workspace.
 *
 * Purpose: Main view for the Recoveries phase. Surfaces POD progress
 * stats (received / adjudicated / admitted / rejected), the POD claims
 * table, and entry points to lodge new claims and adjudicate existing
 * ones via the claim detail slide-over.
 *
 * URL: /dashboard/jobs/:jobId/recoveries
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Scale, Plus, ArrowLeft, MessageSquare } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { cn } from "@/utils/misc";
import { DocumentDetail } from "../gather/documents/DocumentDetail";
import { PodClaimsPanel } from "./PodClaimsPanel";
import { AddPodClaimPanel } from "./AddPodClaimPanel";
import { PodClaimPanel } from "./PodClaimPanel";
import { PodChatSheet } from "./PodChatSheet";
import { ContactDetailPanel } from "../contacts/ContactDetailPanel";
import { fmtMoney } from "./podShared";

export function RecoveriesView({
  jobId,
  onBack,
  initialClaimId,
  onClaimClose,
}: {
  jobId: Id<"jobs">;
  onBack?: () => void;
  /** Opens this claim on mount — used when jumping here from a creditor. */
  initialClaimId?: Id<"podClaims">;
  /** Called when the claim slide-over closes, so the deep-link can clear. */
  onClaimClose?: () => void;
}) {
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] =
    useState<Id<"podClaims"> | null>(initialClaimId ?? null);

  useEffect(() => {
    if (initialClaimId) setSelectedClaimId(initialClaimId);
  }, [initialClaimId]);
  // Held here rather than inside the claim panel: the document viewer is a
  // Sheet, and nesting it inside the claim Sheet renders it behind the
  // parent's overlay.
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  // Same reason as the document viewer: the contact panel is a Sheet, and
  // one nested inside the claim Dialog renders behind its overlay.
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);

  const { data: rawClaims, isLoading } = useQuery(
    convexQuery(api.jobs.recoveries.publicQueries.listPodClaimsForJob, {
      jobId,
    }),
  );
  const { data: jobContacts } = useQuery({
    ...convexQuery(api.contacts.queries.listJobContacts, { jobId }),
    // Only the contact panel's merge picker needs the full list, so it is
    // not subscribed until a contact record is opened.
    enabled: viewingContactId !== null,
  });
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
  );

  // Relatedness is derived from the related parties register rather than
  // stored on the claim, so it is joined on here and follows the register as
  // it fills out over the life of the job.
  const claims = useMemo(() => {
    const rows = rawClaims ?? [];
    if (!relatedFlags?.claims.length) return rows;
    const byClaim = new Map(
      relatedFlags.claims.map(({ claimId, ...match }) => [claimId, match]),
    );
    return rows.map((claim) => {
      const relatedParty = byClaim.get(claim._id);
      return relatedParty ? { ...claim, relatedParty } : claim;
    });
  }, [rawClaims, relatedFlags]);

  // -------------------------------------------------------------------------
  // Header stats
  // -------------------------------------------------------------------------

  const stats = useMemo(() => {
    const received = claims.filter(
      (c) => c.status === "received" || c.status === "adjudicated",
    ).length;
    const adjudicated = claims.filter((c) => c.status === "adjudicated");
    const admittedTotal = adjudicated.reduce(
      (sum, c) => sum + (c.admittedAmount ?? 0),
      0,
    );
    const rejectedTotal = adjudicated.reduce(
      (sum, c) => sum + (c.rejectedAmount ?? 0),
      0,
    );
    return {
      total: claims.length,
      received,
      adjudicated: adjudicated.length,
      admittedTotal,
      rejectedTotal,
    };
  }, [claims]);

  return (
    // Bleed to the page edges like the Investigations workspaces, with the
    // content held to a readable measure instead of stretching to the window.
    <div className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 pt-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mb-1.5 flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Recoveries
              </button>
            )}
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-bold">Proof of Debt Adjudication</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Manage &amp; adjudicate POD claims. Morgan reconciles supporting
              documents with books &amp; records data.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setChatOpen(true)}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Chat
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setAddPanelOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              New POD Claim
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label="PODs received"
            value={`${stats.received} / ${stats.total}`}
          />
          <StatCard
            label="Adjudicated"
            value={`${stats.adjudicated} / ${stats.total}`}
          />
          <StatCard
            label="Admitted"
            value={fmtMoney(stats.admittedTotal)}
            valueClassName="text-emerald-700 dark:text-emerald-400"
          />
          <StatCard
            label="Rejected"
            value={fmtMoney(stats.rejectedTotal)}
            valueClassName="text-red-700 dark:text-red-400"
          />
        </div>

        {/* Claims table */}
        <PodClaimsPanel
          jobId={jobId}
          claims={claims}
          isLoading={isLoading}
          selectedClaimId={selectedClaimId}
          onSelect={setSelectedClaimId}
          onAdd={() => setAddPanelOpen(true)}
        />
      </div>

      {/* New claim form */}
      <AddPodClaimPanel
        jobId={jobId}
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        onCreated={(id) => {
          setAddPanelOpen(false);
          setSelectedClaimId(id);
        }}
      />

      {/* Claim detail / adjudication slide-over */}
      <PodClaimPanel
        claimId={selectedClaimId}
        claims={claims}
        onClose={() => {
          setSelectedClaimId(null);
          onClaimClose?.();
        }}
        onViewDocument={setViewingDocumentId}
        onViewContact={setViewingContactId}
        onMerged={(targetId) => setSelectedClaimId(targetId)}
      />

      {/* Source document viewer */}
      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Creditor contact record — identity, details, and the mail threads
          between the office and them. */}
      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={jobContacts ?? []}
      />

      {/* POD chat sidesheet — mounted here as a sibling of the claim
          slide-over, for the same reason the document viewer is: a Sheet
          nested inside another Sheet renders behind its parent's overlay. */}
      <PodChatSheet open={chatOpen} onOpenChange={setChatOpen} jobId={jobId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-background px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={cn("mt-1 text-xl font-bold tabular-nums", valueClassName)}>
        {value}
      </p>
    </div>
  );
}
